import { useState, useCallback } from "react";
import { GetAllCollectionsOfUserOutput } from "../admin/dashboards/operations";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import { useQuery, getAllCollectionsOfUser, updateMiqCollection } from "wasp/client/operations";
import { createShopifyToInternalTableUrl } from "../admin/dashboards/urls";
import { updateCurrentUser } from "wasp/client/operations";
import { useDispatch } from "react-redux";
import { setIsGetStartedDialogOpen, setImportShopifyInitialLoading } from "../store/flowSlice";
import { analytics } from "../analytics";

export type ImportShopifyResponse = {
  success: boolean;
  message?: string;
};

type UseImportShopifyHook = {
  importShopify: () => Promise<ImportShopifyResponse>;
  isImportLoading: boolean;
};

export const useImportShopify = (): UseImportShopifyHook => {
  const dispatch = useDispatch();
  const { currentUser, currentCollection } = useSelector((state: RootState) => state.user);
  const [isImportLoading] = useState(false);
  const {
    data: collections,
    isLoading: isCollectionsLoading,
    error: isCollectionsError,
    refetch: refetchCollections,
  } = useQuery(getAllCollectionsOfUser);
  

  const importShopify =
    useCallback(async (): Promise<ImportShopifyResponse> => {
      analytics.sendAnalysisStartedEvent({
        email: currentUser?.email || "unknown",
      })
      dispatch(setIsGetStartedDialogOpen(false));
      let _collections: GetAllCollectionsOfUserOutput[] =
        collections as GetAllCollectionsOfUserOutput[];
      if (!_collections || _collections.length === 0) {
        console.error("No collections found for the user. Contact admin!");
        return {
          success: false,
          message: "No collections found. Contact admin.",
        };
      }

      if (!currentUser?.email) {
        console.error("User email is missing. Cannot proceed with import.");
        return {
          success: false,
          message: "User email is missing. Cannot proceed.",
        };
      }

      dispatch(setImportShopifyInitialLoading(true));
      try {
        const collection = _collections[0];
      
        if(!currentCollection?.collection?.miqShopifyDomain) {
          console.error("Shop domain is missing. Cannot proceed with import.");
          return {
            success: false,
            message: "Shop domain is missing. Cannot proceed.",
          };
        }

        const url = createShopifyToInternalTableUrl(
          // Define this function outside
          currentUser.email,
          collection.name,
          currentCollection?.collection.miqShopifyDomain
        );

        const jobRes = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!jobRes.ok) {
          console.error(
            `Failed to start Shopify import job. Status: ${jobRes.status}`
          );
          return {
            success: false,
            message: "Failed to start Shopify import job.",
          };
        }

        const jobData = (await jobRes.json()) as {
          job_id: string;
          status: string;
        };
        console.log("Shopify import job started:", jobData);

        await updateMiqCollection({
          id: collection.id,
          miqHasStartedShopifyImport: true,
          miqShopifyJobId: jobData.job_id,
        })

        return { success: true };
      } catch (error) {
        console.error("An error occurred during Shopify import:", error);
        return {
          success: false,
          message: "An error occurred during Shopify import.",
        };
      } finally {
        dispatch(setImportShopifyInitialLoading(false));
      }
    }, [collections, currentCollection?.collection?.miqShopifyDomain, setImportShopifyInitialLoading]);

  return { importShopify, isImportLoading };
};
